// luxe（暗夜轻奢）前端皮肤辅助（039_Spec §4.1 · T3.3）
// 背景：v2geo.generated.ts 由 dump-v2-geometry.ts 生成，其 V2Skin 快照尚未导出 luxe 新增字段
//（blush / decorCircle / pageDeep / accentLight / inkDeep），且 theme 快照按 swiss/magazine 字段
// 裁剪——luxe 下只剩 ink/paper（paperTint 为 "#undefined" 坏值，禁止消费）。
// 在 dump 脚本补齐之前，luxe 扩展字段一律从 tokens.generated.ts 的 THEMES 取（色值补 #）。
// LuxeDecorCircle 坐标对照后端 _shared/pptx/v2/layouts/luxe-decor.ts（1:1 移植）。

import { THEMES, type LuxeTheme } from "../../lib/tokens.generated";
import { px } from "./shared";
import type { V2Skin } from "../../lib/v2geo.generated";

/** 是否 luxe 皮肤（V2Skin.style 类型滞后于生成物，运行时值已是 "luxe"） */
export function isLuxe(skin: V2Skin): boolean {
  return (skin.style as string) === "luxe";
}

export interface LuxeExt {
  ink: string; inkDeep: string; paper: string;
  accentLight: string; blush: string; grey1: string;
}

/** luxe 主题扩展字段（带 # 色值）；非 luxe 返回 null */
export function luxeExt(skin: V2Skin): LuxeExt | null {
  if (!isLuxe(skin)) return null;
  const id = (skin.theme as { id?: string }).id ?? "";
  const t = THEMES[id];
  if (!t || t.style !== "luxe") return null;
  const l = t as LuxeTheme;
  const h = (v: string) => (v.startsWith("#") ? v : `#${v}`);
  return {
    ink: h(l.ink), inkDeep: h(l.inkDeep), paper: h(l.paper),
    accentLight: h(l.accentLight), blush: h(l.blush), grey1: h(l.grey1),
  };
}

export type LuxeDecorVariant = "cover" | "divider" | "statement" | "closing";

/** 裸金装饰圆（DOM 版；对照后端 luxe-decor.ts）。仅 luxe 渲染；父容器 overflow:hidden 裁切出画布部分 */
export function LuxeDecorCircle({ skin, variant }: { skin: V2Skin; variant: LuxeDecorVariant }) {
  if (!isLuxe(skin)) return null;
  const accent = skin.accent;
  const W = 13.333;
  const H = 7.5;
  const circle = (x: number, y: number, d: number, ring = false) => (
    <div style={{
      position: "absolute", left: px(x), top: px(y), width: px(d), height: px(d),
      borderRadius: "50%",
      ...(ring ? { border: `1px solid ${accent}` } : { background: accent }),
    }} />
  );
  switch (variant) {
    case "cover":
      // 右上裸金大圆 + 左下描边小环（luxe-decor.ts cover 分支）
      return <>{circle(W - 1.55, -2.3, 4.6)}{circle(-1.1, H - 1.55, 2.4, true)}</>;
    case "divider":
      // 右缘半圆
      return circle(W - 1.9, H / 2 - 1.9, 3.8);
    case "statement":
      // 右下小圆
      return circle(W - 2.17, H - 2.1, 1.5);
    case "closing":
      // 左上大圆（落在墨黑左半屏，与封面闭环）
      return circle(-2.2, -2.2, 4.2);
  }
}
